import styles from "./RevenueRow.module.scss";
import { Field, useForm } from "react-final-form";
import { FieldArray } from "react-final-form-arrays";
import { financialYearLabel } from "./RevenueDetails";
import { useContext, useEffect, useRef, useState } from "react";
import {
  UnderwritingSteps,
  pdfProtectionStates,
  uploadStates,
} from "../../../interfaces/interfaces";
import { getIn } from "final-form";
import MeritoButton from "../../../components/common/MeritoButtons/MeritoButtons";
import { Modal } from "reactstrap";
import ProvisionalStatement from "./ProvisionalStatement";
import { MdOutlineFileUpload as UploadIcon } from "react-icons/md";
import { UnderwritingFormContext, UnderwritingFormContextType } from "../context/UnderwritingFormContext";
import FinancialFileItem from "./FinancialFileItem";
import { checkPDFProtection } from "../components/utils/functions";
import API from "../../../rest/api";
import { v1 as uuidv1 } from "uuid";
import { RiErrorWarningFill } from "react-icons/ri";
import { isFyClassificationJobProcessing } from "./revenueHelper";

type Props = {
  fieldName: string;
  fieldIndex: number;
  newUploadedDocIds: number[];
  setNewUploadedDocIds: React.Dispatch<React.SetStateAction<number[]>>;
  deleteDocIds: number[];
  setDeleteDocIds: React.Dispatch<React.SetStateAction<number[]>>;
};

const RevenueRow = ({
  fieldName,
  fieldIndex,
  newUploadedDocIds,
  setNewUploadedDocIds,
  deleteDocIds,
  setDeleteDocIds,
}: Props) => {
  const { getState, change } = useForm();
  const { handleUnderwritingFileJobs, sendFileJobToApi, revenueClassificationJobs, clearRevenueClassificationJob, clearAllRevenueClassificationData } = useContext(
    UnderwritingFormContext
  ) as UnderwritingFormContextType;

  const fyYear = getIn(getState()?.values, `${fieldName}.financialYear`);
  const fyClassificationData = revenueClassificationJobs?.[fyYear] ?? null;
  const classificationResult = fyClassificationData?.classificationResult ?? null;
  const classificationErrorDetails = fyClassificationData?.classificationErrorDetails ?? null;

  const isFyClassificationProcessing = isFyClassificationJobProcessing(
    fyClassificationData
  );

  useEffect(() => {
    if (!classificationResult || deleteDocIds.length === 0) return;

    const files: { uploadedFile?: { docId?: number } }[] =
      getIn(getState().values, `${fieldName}.auditedFinancials`) ?? [];
    const hasNoUploadedFiles = !files.some((f) => !!f?.uploadedFile?.docId);
    if (hasNoUploadedFiles) {
      clearAllRevenueClassificationData(fyYear);
      return;
    }

    const errorDocIds = classificationResult
      .filter((entry: { docId: number; error: any }) => entry.error && Object.keys(entry.error).length > 0)
      .map((entry: { docId: number; error: any }) => entry.docId);
    if (errorDocIds.length === 0) return;
    const allErrorFilesDeleted = errorDocIds.every((id: number) => deleteDocIds.includes(id));
    if (allErrorFilesDeleted) {
      clearRevenueClassificationJob(fyYear);
    }
  }, [deleteDocIds, classificationResult]);

  const [isOpenProvisional, setIsOpenProvisional] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const financialYearDetails = getIn(
    getState()?.values,
    `${fieldName}.financialYearDetails`
  );
  const isAuditedFinancials = getIn(
    getState()?.values,
    `${fieldName}.isAuditedFinancials`
  );
  const isFyPassed = financialYearDetails?.fyPassed;
  console.log("financialYearDetails", financialYearDetails);
  const badgeText = !isFyPassed
    ? "Provisional (MIS / Tally export)"
    : financialYearDetails?.isAuditedFinancialRequired
    ? "Audited"
    : "Provisional";

  const uploadFileToS3 = async (
    currentFieldName: string,
    fileData: File,
    fileFormJob: any
  ) => {
    try {
      const formData = new FormData();
      formData.append("documentType", "financials");
      formData.append("file", fileData);
      const data = await API.post("/app/uploadDocs", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      }).then((res) => res.data);

      const isPdfProtected = await checkPDFProtection(fileData);
      let jobDetails = null;

      if (fileData.type === "application/pdf") {
        if (isPdfProtected === pdfProtectionStates.passwordNotRequired) {
          jobDetails = await sendFileJobToApi(
            UnderwritingSteps.revenue,
            currentFieldName,
            {
              s3FileKey: data?.Key,
              fileMetaData: {
                fileName: fileData?.name,
                fileType: fileData?.type,
                requiredPassword: false,
                password: null,
              },
              status: uploadStates.doneUploading,
            }
          );
        } else if (isPdfProtected === pdfProtectionStates.passwordRequired) {
          jobDetails = await sendFileJobToApi(
            UnderwritingSteps.revenue,
            currentFieldName,
            {
              s3FileKey: data?.Key,
              fileMetaData: {
                fileName: fileData?.name,
                fileType: fileData?.type,
                requiredPassword: true,
                password: null,
              },
              status: uploadStates.requiredPassword,
            }
          );
        }
      } else {
        jobDetails = await sendFileJobToApi(
          UnderwritingSteps.revenue,
          currentFieldName,
          {
            s3FileKey: data?.Key,
            fileMetaData: {
              fileName: fileData?.name,
              fileType: fileData?.type,
              requiredPassword: false,
              password: null,
            },
            status: uploadStates.doneUploading,
          }
        );
      }

      setNewUploadedDocIds((prev) => [...prev, jobDetails?.docId]);
      change(`${currentFieldName}`, {
        ...fileFormJob,
        uploadedFile: {
          fileKey: data?.Key,
          fileName: fileData?.name,
          fileType: fileData?.type,
          docId: jobDetails?.id,
        },
      });
    } catch (error) {
      console.log("error", error);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newFiles: File[] = Array.from(event.target.files || []);

    const auditedFinancialsFieldName = `${fieldName}.auditedFinancials`;
    const existingFiles =
      getIn(getState().values, auditedFinancialsFieldName) || [];
    const updatedFiles = [...existingFiles];

    newFiles.forEach((file) => {
      const fileFormObj = { selectedFile: file, uploadedFile: null };
      updatedFiles.push(fileFormObj);
      const currentFieldName = `${auditedFinancialsFieldName}[${updatedFiles.length - 1}]`;
      const fileJob = {
        jobId: uuidv1(),
        step: UnderwritingSteps.revenue,
        section: "financials",
        formFieldName: currentFieldName,
        s3FileKey: null,
        fileMetaData: {
          fileName: file.name,
          fileType: file.type,
          requiredPassword: false,
          password: null,
        },
        status: uploadStates.uploading,
      };
      change(currentFieldName, fileFormObj);
      handleUnderwritingFileJobs(
        UnderwritingSteps.revenue,
        currentFieldName,
        fileJob
      );
      uploadFileToS3(currentFieldName, file, fileFormObj);
    });

    // Reset input so same file can be re-uploaded if needed
    event.target.value = "";
  };
  console.log("isAUDITEDFINANCIALS", isAuditedFinancials);

  return (
    <>
      <div className={styles.revenueCard}>
        <div className={styles.revenueCardHeader}>
          <Field name={`${fieldName}.financialYear`}>
            {(props) => (
              <div className={styles.revenueYear}>
                <span>{`FY ${financialYearLabel(props.input.value)}`}</span>
                {!isFyPassed && <span>(YTD)</span>}
              </div>
            )}
          </Field>
          <div className={styles.requiredDocTypeBadge}>{badgeText}</div>
        </div>

        <div className={styles.uploadBtnContainer}>
          {isAuditedFinancials && (
            <input
              type="file"
              multiple
              ref={fileInputRef}
              onChange={handleFileChange}
              style={{ display: "none" }}
              accept=".pdf,.xls,.xlsx"
            />
          )}
          <MeritoButton
            type="primary-1"
            btnText="Upload Financials"
            size="md"
            disabled={isFyClassificationProcessing}
            onClick={() => {
              isAuditedFinancials
                ? fileInputRef.current?.click()
                : setIsOpenProvisional(true);
            }}
          />
          {isFyClassificationProcessing && (
            <div className={styles.processingText}>Processing your files. Please wait...</div>
          )}
        </div>
        <Field
          name={`${fieldName}.auditedFinancials`}
          subscription={{ value: true, error: true, touched: true }}
        >
          {({ input, meta }) => {
            const files = Array.isArray(input.value) ? input.value : [];
            const hasUploadedFinancialDocs = files.some(
              (f: { uploadedFile?: { docId?: number } }) =>
                !!f?.uploadedFile?.docId
            );
            const showFirstFyMissingDocsError =
              isAuditedFinancials &&
              fieldIndex === 0 &&
              !hasUploadedFinancialDocs &&
              !!meta?.error &&
              !!meta?.touched;
            const showClassificationError =
              isAuditedFinancials &&
              hasUploadedFinancialDocs &&
              !isFyClassificationProcessing &&
              classificationErrorDetails?.error_state &&
              Object.keys(classificationErrorDetails.error_state).length > 0;
            if (!showFirstFyMissingDocsError && !showClassificationError) return null;
            const title = showFirstFyMissingDocsError
              ? "Financials not uploaded."
              : classificationErrorDetails.error_state.title;
            const action = showFirstFyMissingDocsError
              ? "Upload provisional Balance Sheet and P&L."
              : classificationErrorDetails.error_state.action;
            return (
              <div className={styles.errorMainContainer}>
                <div className={styles.errorContainer}>
                  <div className={styles.errorTitle}>
                    <RiErrorWarningFill className={styles.errorTitleIcon} />
                    <div className={styles.errorTitleText}>
                      {title}
                    </div>
                  </div>
                  <div className={styles.errorAction}>
                    {action}
                  </div>
                </div>
              </div>
            );
          }}
        </Field>
        

        {isAuditedFinancials && (
          <FieldArray name={`${fieldName}.auditedFinancials`}>
            {({ fields }) =>
              fields.length ? (
                <div className={styles.uploadedFilesList}>
                  {fields.map((innerFieldName, index) => (
                    <FinancialFileItem
                      key={innerFieldName}
                      outerFieldName={`${fieldName}.auditedFinancials`}
                      fieldName={innerFieldName}
                      stepType={UnderwritingSteps.revenue}
                      newUploadedDocIds={newUploadedDocIds}
                      setNewUploadedDocIds={setNewUploadedDocIds}
                      deleteDocIds={deleteDocIds}
                      setDeleteDocIds={setDeleteDocIds}
                      fieldIndex={index}
                      fyClassificationResult={classificationResult}
                      fyClassificationErrorDetails={classificationErrorDetails}
                      isJobProcessing={isFyClassificationProcessing}
                    />
                  ))}
                </div>
              ) : null
            }
          </FieldArray>
        )}
        <div className={styles.cardBottomSpacer} aria-hidden />
      </div>
      <Modal
        isOpen={isOpenProvisional}
        toggle={() => {
          setIsOpenProvisional(!isOpenProvisional);
        }}
        size="lg"
        fullscreen={true}
        keyboard={false}
      >
        <ProvisionalStatement
          setIsOpen={setIsOpenProvisional}
          fieldName={fieldName}
          financialDetails={financialYearDetails}
          isOpen={isOpenProvisional}
        />
      </Modal>
    </>
  );
};

export default RevenueRow;
